import { useEffect, useState } from 'react';

import * as api from '@/features/backlog/api';
import { Item, Status } from '@/features/backlog/types';
import { priorityMeta, taskTypeMeta } from '@/features/backlog/constants';
import { useAppData } from '@/app-data';
import MemberAvatar from '@/components/member-avatar';
import { Comment, ExternalLink, PriorityChevron, TaskTypeGlyph } from '@/components/ui/icons';

/**
 * A read-only, kanban-style card for a board item embedded in a wiki page: type glyph + key badge +
 * board pill + title + a status swatch / priority / assignee footer + labels. Fetches the item (and its
 * board's statuses, since statuses are per-board) on mount; degrades to a link chip if it's gone. Mirrors
 * the look of the kanban card + item detail, without any of the editing controls.
 */
export function WikiItemCard({ itemId, fallbackLabel, href }: { itemId: string; fallbackLabel: string; href: string }) {
  const { members, boards } = useAppData();
  const [item, setItem] = useState<Item | null>(null);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getItem(itemId)
      .then((it) => {
        if (cancelled) return undefined;
        setItem(it);
        return api.listStatuses(it.board_id).then((s) => !cancelled && setStatuses(s));
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const board = item ? boards.find((b) => b.id === item.board_id) : undefined;
  const openHref = board && item ? `/${board.slug}/${item.key}` : href;
  const open = () => openHref && window.open(openHref, '_blank', 'noopener,noreferrer');

  if (failed) {
    return (
      <a href={openHref} onClick={(e) => { e.preventDefault(); open(); }} className="my-1 flex items-center gap-2 rounded-lg border border-line bg-surface-inset px-3 py-2 text-[13px] text-fg-muted no-underline transition-colors hover:bg-layer" contentEditable={false}>
        <span className="text-fg-faint">#</span>
        <span className="min-w-0 flex-1 truncate">{fallbackLabel}</span>
        <span className="shrink-0 text-[11px] uppercase tracking-wide text-fg-faint">item</span>
      </a>
    );
  }
  if (!item) return <div className="my-1.5 h-[86px] animate-pulse rounded-xl border border-line bg-surface-inset" contentEditable={false} />;

  const tt = taskTypeMeta(item.type);
  const pr = priorityMeta(item.priority);
  const st = statuses.find((s) => s.value === item.status);
  const assignee = item.assignee_id ? members.find((m) => m.id === item.assignee_id) ?? null : null;

  return (
    <div contentEditable={false} className="my-1.5 select-none rounded-xl border border-line bg-surface-raised p-3 transition-colors hover:border-line-strong">
      <div className="flex items-center gap-1.5">
        <TaskTypeGlyph type={item.type} color={tt.color} size={15} />
        <span className="item-key shrink-0 rounded px-1.5 py-0.5 tnum text-[10px] font-semibold">{item.key}</span>
        {board ? <span className="shrink-0 rounded bg-layer px-1.5 py-0.5 text-[10px] font-medium text-fg-muted ring-1 ring-line">{board.name}</span> : null}
        <button type="button" onClick={open} className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer hover:text-fg" title="Open item">
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-1.5 text-[14px] font-medium leading-snug text-fg-strong">{item.title}</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11.5px] text-fg-muted">
        <span className="inline-flex items-center gap-1.5" title={st?.label ?? item.status}>
          <span className="h-2.5 w-2.5 rounded-full ring-1 ring-inset ring-black/15" style={{ background: st?.color ?? '#8b8b8b' }} />
          {st?.label ?? item.status}
        </span>
        <span className="inline-flex items-center gap-1" title={`${pr.label} priority`}>
          <PriorityChevron level={pr.level} color={pr.color} /> {pr.label}
        </span>
        {assignee ? (
          <span className="inline-flex items-center gap-1.5">
            <MemberAvatar member={assignee} size={18} /> {assignee.name}
          </span>
        ) : null}
        {(item.comment_count ?? 0) > 0 ? (
          <span className="inline-flex items-center gap-1 text-fg-faint">
            <Comment className="h-3.5 w-3.5" /> {item.comment_count}
          </span>
        ) : null}
      </div>
      {item.labels?.length ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {item.labels.map((l) => (
            <span key={l} className="rounded bg-accent/12 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-accent">{l}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
